/*!
 * Copyright 2022 Thorne Health Lab. All rights reserved.
 */
import React, { useEffect, useState } from 'react';
import {
  find,
  forEach,
  get,
  has,
  isArray,
  lowerCase,
  map,
  filter,
  remove,
  isEqual
} from 'lodash';

import DoubleText from './QuestionComponents/DoubleText';
import SingleText from './QuestionComponents/SingleText';
import Slider from './QuestionComponents/Slider';
import Radio from './QuestionComponents/Radio';
import Checkbox from './QuestionComponents/Checkbox';
import TextDropdown from './QuestionComponents/TextDropdown';
import EditSelect from './QuestionComponents/EditSelect';
import DoubleTextDropdown from './QuestionComponents/DoubleTextDropdown';
import Dropdown from './QuestionComponents/Dropdown';
import TextRadio from './QuestionComponents/TextRadio';
import RadioGrid from './QuestionComponents/RadioGrid';
import DropdownRadio from './QuestionComponents/DropdownRadio';
import CheckboxGrid from './QuestionComponents/CheckboxGrid';
import Message from './QuestionComponents/Message';
import TimePicker from './QuestionComponents/TimePicker';
import TimeEditor from './QuestionComponents/TimeEditor';
import * as surveyParser from '../../../utils/survey-parser.js';
import Element from '../../../components/element';
import { colors } from '../../../config/theme';
import Button from '../../../components/Button';
import Flex from '../../../components/flex';
import Text from '../../../components/text';
import FilterList from './QuestionComponents/FilterList/index';
import { ASSESSMENTS_URL } from '../../../api/endpoints';
import { useDispatch, useSelector } from 'react-redux';
import { apiRequest } from '../../../api/actions';
import { useNavigate } from 'react-router-dom';
import {
  setActiveAssessment,
  setSurveysLastCompletedAt,
  setSurveysLastModifiedOn
} from '../../../root/actions';
import { isNullOrUndefined } from '../../../utils';
import medications from '../../../jsons/medicationList.json';
import moment from 'moment';
import { Line } from 'rc-progress';

const components = {
  SingleText,
  DoubleText,
  Slider,
  Radio,
  Checkbox,
  Dropdown,
  TextDropdown,
  EditSelect,
  DropdownRadio,
  TextRadio,
  RadioGrid,
  CheckboxGrid,
  DoubleTextDropdown,
  TimePicker,
  TimeEditor,
  Message,
  FilterList
};

const getReduxData = state => {
  return {
    allSurveys: state.containers.app.get('activeAssessments'),
    sortedAssessments: state.containers.app.get('sortedAssessments'),
    surveysLastCompletedAt: state.containers.app.get('surveysLastCompletedAt'),
    user: state.containers.app.get("user"),
  };
};

const Survey = props => {

  const { survey, isMobile, editSurveyData } = props;
  const { steps } = survey || {};
  const [currentStep, setCurrentStep] = useState(-1);
  const [answers, setAnswers] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { allSurveys, sortedAssessments, surveysLastCompletedAt, user } = useSelector(
    getReduxData
  );
  const [medicationList, setMedicationList] = useState(medications);
  const [isOptionalAnswered, setIsOptionalAnswered] = useState(false);
  const [selectedMedications, setSelectedMedications] = useState([]);

  /**
   *
   * @param {*} questionId
   * @param {*} answer List  of family member names if selected
   * @param {*} answered
   * @param {*} routeMap
   * @param {*} key
   */

  const storeAnswer = (
    questionId,
    answer,
    answered,
    routeMap,
    key,
    isOptAnswered = false
  ) => {
    answer.answered = answered;
    //Below is for Family history
    if (
      questionId.indexOf('family') > -1 &&
      isNullOrUndefined(answer?.noneOfTheAbove)
    ) {
      if (answer.noneOfTheAbove) {
        // If the checkbox "none of the above" is true, perform checking all 'N/A' checkbox checked in illness questionnaire
        for (let key in answer) {
          if (isArray(answer[key])) {
            answer[key] = ['N/A'];
          }
        }
      } else if (
        isNullOrUndefined(answers[questionId]?.noneOfTheAbove) &&
        answers[questionId]?.noneOfTheAbove &&
        !answer.noneOfTheAbove
      ) {
        // If earlier the "none of the above" is true and later changed to false, then uncheck all 'N/A' checckoxes in illnes questinarre
        for (let key in answer) {
          if (isArray(answer[key])) {
            answer[key] = [];
          } else if (key === 'answered') {
            answer[key] = false;
          }
        }
      }
    }
    //Below is for checkbox list applicable for medical and allergies & medication surveys
    if (
      questionId.indexOf('medical') > -1 ||
      questionId.indexOf('family') > -1 ||
      questionId.indexOf('medication') > -1
    ) {
      let list = answer?.selected;
      if (Array.isArray(list) && list.length > 1) {
        if (list[0] === 'N/A') {
          list.splice(0, 1);
        } else {
          const isNAExist =
            list.filter(function (val) {
              return val === 'N/A';
            }).length > 0;
          if (isNAExist) {
            list.splice(0, list.length - 1);
          }
        }
      }
    }

    if (routeMap && key) {
      answer.answerKey = key;
    }
    setIsOptionalAnswered(isOptAnswered);
    const newAnswers = { ...answers, [questionId]: answer };
    setAnswers(newAnswers);
    //Below logic for allergies and medication as it has filter with checkbox, input field and check box with NA.
    //TODO: Need to refactor below code
    if (questionId.indexOf('medication_s1') > -1) {
      const medicationsS2Items = {
        medication_s1BxU1: { selected: [], answered: true },
        medication_s1LCr5: { value: '', answered: true },
        medication_s154OE: { selected: [], answered: true }
      };
      let result = { [questionId]: answer };
      Object.keys(medicationsS2Items).forEach(item => {
        if (
          Object.keys(answers).filter(keyName => {
            return keyName === item;
          }).length === 0
        ) {
          if (item !== questionId) {
            result[item] = medicationsS2Items[item];
          }
        } else if (item === questionId) {
          const tempAnswer = { ...answer, answered: true };
          result[item] = tempAnswer;
        }
      });
      if (questionId === 'medication_s154OE'){
        if(result[questionId]?.selected.length > 0){
          answers['medication_s1BxU1'] = {selected: []};
          setMedicationList(medicationList.map(item => {
            return { ...item, checked: false }}))
        }

        answers['medication_s1LCr5']= {value: ''};
      } else if(['medication_s1BxU1', 'medication_s1LCr5'].includes(questionId) ){
        answers['medication_s154OE']= {selected: []}
      }
         
      const newAnswers1 = { ...answers, ...result };
      setAnswers(newAnswers1);
    }
  };

  const analyzeQuestionDisplay = (answer, routeMap) => {
    if (!routeMap || !answer) {
      return;
    }
    const value = answer[answer.answerKey];
    const hideQuestionIds = get(routeMap, `[${value}].hide`);
    const showQuestionIds = get(routeMap, `[${value}].show`);
    map(hideQuestionIds, questionId => {
      const question = find(steps[currentStep].questions, [
        'questionId',
        questionId
      ]);
      question.display = false;
    });
    map(showQuestionIds, questionId => {
      const question = find(steps[currentStep].questions, [
        'questionId',
        questionId
      ]);
      question.display = true;
    });
  };

  const isStepValidated = () => {
    let stepValid = true;
    const { questions } = steps[currentStep];
    for (let i = 0; i < questions.length; i++) {
      const question = questions[i];
      const { validation } = question.details;
      if (validation) {
        const ans = answers[question.questionId];
        if (validation.type === 'difference') {
          const compareAns = answers[validation.questionId];
          if (isEqual(ans, compareAns)) {
            stepValid = false;
            const compareQue = find(questions, [
              'questionId',
              validation.questionId
            ]);
            window.alert(
              `Answers of below questions should not be same \n Question: ${question.details.question} \n Question: ${compareQue.details.question}`
            );
            break;
          }
        }
        if (validation.type === '>0') {
          if (!Number(ans.hours)) {
            stepValid = false;
            window.alert(
              `Answer of the question is not valid \n Question: ${question.details.question} `
            );
            break;
          }
        }
      }
    }
    return stepValid;
  };

  const gotoNextStep = () => {
    isStepValidated() && setCurrentStep(currentStep + 1);
  };

  const saveSurvey = () => {
    if (isStepValidated()) {
      sendSurvey();
      navigate('/landing/assessments');
    }
  };

  const mapSortedAssessmentsToAllSurveys = () => {
    const keys = [
      'familyHistory',
      'socialHistory',
      'medicalHistory',
      'medications',
      'sleep',
      'physicalActivity',
      'nutrition',
      'mentalHealth'
    ];
    for (let i = 0; i < 8; i++) {
      allSurveys[keys[i]] = sortedAssessments[i];
    }
  };

  const sendSurvey = () => {
    const currentDateTime = moment.utc().toISOString();
    mapSortedAssessmentsToAllSurveys();
    const currentSurvey = allSurveys[survey.shortName];
    currentSurvey.answers.raw[0] = answers;
    currentSurvey.completed = surveyParser.isSurveyCompleted(
      currentSurvey,
      answers
    );

    const isAllSurveysHaveCompletedTrue = Object.values(allSurveys)
      .slice(0, 8)
      .every(survey => survey.completed);
    if (isFinishSurvey() && isAllSurveysHaveCompletedTrue) {
      dispatch(setSurveysLastCompletedAt(currentDateTime));
      allSurveys['surveysLastCompletedAt'] = currentDateTime;
      localStorage.removeItem('isUserUpdatedSurvey');
      localStorage.setItem('isFinishSurveyBtnCLicked', 'yes');
    } else {
      localStorage.setItem('isUserUpdatedSurvey', 'yes');
      allSurveys['surveysLastUpdatedAt'] = currentDateTime;
      allSurveys['surveysLastCompletedAt'] = surveysLastCompletedAt;
    }
    allSurveys['surveysLastModifiedOn'] = currentDateTime;
    dispatch(setSurveysLastModifiedOn(currentDateTime));

    const request = {
      method: 'put',
      url: ASSESSMENTS_URL,
      data: allSurveys
    };
    const meta = {
      onSuccess: res => {
        if (isFinishSurvey() && isAllSurveysHaveCompletedTrue) {
          localStorage.removeItem('isFinishSurveyBtnCLicked');
          // console.log('Finish Clicked: ***************************** ')
        } else {
          // navigate('/landing/assessments');
        }
      }
    };
    dispatch(apiRequest(request, meta));
    const {
      survey: { shortName }
    } = props;

    let parsedAnswers = {};
    const rawAnswers = props.survey.answers.raw;
    const parsed = props.survey.answers.parsed;
    parsedAnswers = surveyParser[shortName](rawAnswers, parsed);
    editSurveyData(
      allSurveys,
      true,
      null,
      true,
      allSurveys[shortName].route,
      parsedAnswers,
      shortName
    );
  };

  const renderSurvey = () => {
    let nodes = [];
    if (currentStep > -1) {
      const step = steps?.[currentStep] || {};
      if (step) {
        const { questions } = step;
        let hideQuestionIdsList = [];
        for (let i = 0; i < questions?.length; i++) {
          const question = questions[i];
          const routeMap = get(question, 'details.routeMap');
          const answer = answers[question.questionId];
          let skipQuestions = false;
          if (answer?.answerKey) {
            const value = answer[answer.answerKey];
            const hideIds = get(routeMap, `[${value}].hide`);
            const showIds = get(routeMap, `[${value}].show`);
            if (hideQuestionIdsList.length > 0 && showIds?.length > 0) {
              hideQuestionIdsList.forEach(item => {
                showIds.forEach(item1 => {
                  if (item === item1) {
                    skipQuestions = true;
                  }
                });
              });
            } else {
              if (hideIds?.length > 0) {
                hideQuestionIdsList.push(...hideIds);
              }
            }
          }
          if (!skipQuestions) {
            analyzeQuestionDisplay(
              answers[question.questionId],
              get(question, 'details.routeMap')
            );
          }
          if (has(question, 'display') && !question.display) {
            continue;
          }
          const details = question.details;
          const Component = components[question.type];

          const rawAnswers =
            answers[question.questionId] ||
            get(survey, `answers.raw[0].${question.questionId}`, null);
          const customClassOther = question?.customClassOther;
          nodes.push(
            <Component
              {...details}
              index={`${currentStep}_${i}`}
              key={`${step.stepId}_${i}`}
              stepId={step.stepId}
              questionId={question.questionId}
              className={`question-block`}
              rawAnswers={rawAnswers}
              isMobile={isMobile}
              notRequired={details.notRequired}
              // checkSurveyState={this.checkSurveyState}
              checkSurveyState={() => { }}
              storeAnswer={storeAnswer}
              medicationList={medicationList}
              selectedList={rawAnswers || selectedMedications}
              setSelectedMedications={setSelectedMedications}
              customClassOther={customClassOther}
            />
          );
        }
      }
    }

    return nodes;
  };

  const isFinishSurvey = () => {
    const newSurveys = [...sortedAssessments];
    remove(newSurveys, { shortName: survey.shortName });
    const completed = surveyParser.isAllSurveysCompleted(newSurveys);
    return completed;
  };
  const nextSurveyOrFinishSurvey = () => {
    sendSurvey();
    let nextSurvey = '';
    forEach(sortedAssessments, assessment => {
      if (assessment.shortName === survey.shortName) {
        return;
      }
      // const completed = surveyParser.isSurveyCompleted(
      //   assessment,
      //   get(assessment, 'answers.raw[0]', {})
      // );
      const completed = assessment.completed;
      if (!completed) {
        nextSurvey = assessment;
        return false;
      }
    });

    if (nextSurvey) {
      dispatch(setActiveAssessment(nextSurvey));
      navigate(nextSurvey._meta.route);
    } else {
      navigate(user?.visits.length <= 0 ?
        '/landing/appointment/view' :
        '/landing/assessments');
    }
  };

  const calculateProgress = () => {
    let value = 0;

    let completedSteps = filter(steps, each => {
      return each.completed;
    });

    if (
      completedSteps?.length > 0 &&
      steps?.length === completedSteps?.length
    ) {
      value = (completedSteps?.length / steps?.length) * 100;
    } else if (answers) {
      value = (currentStep / steps?.length) * 100;
    }
    return Math.round(value);
  };

  useEffect(() => {
    // recording all the answers if any in the begining
    const existingAnswers = get(survey, 'answers.raw[0]', {});
    setAnswers({ ...existingAnswers });

    // setting the current step
    let stepNo = -1;
    forEach(steps, (step, index) => {
      if (!step.completed) {
        stepNo = index;
        return false;
      }
    });
    if (stepNo === -1) {
      stepNo = 0;
    }
    setCurrentStep(stepNo); 
  }, [survey]);

  useEffect(()=>{
    if (selectedMedications.length > -1 && answers['medication_s154OE']){
      answers['medication_s154OE'].selected = [];
    }
  }, [selectedMedications])

  return (
    <>
      <Flex align="start" justify="space-between">
        <Element
          type={Text}
          attrs={{ family: 'GTAR', size: '12px', color: colors.dustyGray }}
          marginBottom="20px"
        >
          Patient Surveys > {survey?.title} > Step {currentStep + 1} of{' '}
          {steps?.length}
        </Element>
        <Flex align="center">
          <Text family="GTAR" size="12px" color="#979797" marginRight="5px">
            {calculateProgress()}%
          </Text>
          <Line
            percent={calculateProgress()}
            strokeWidth={1}
            strokeColor="#8b84d7"
            trailColor="#eeeeee"
            style={{ width: '144px', height: '8px', borderRadius: '4px' }}
          />
        </Flex>
      </Flex>

      <Element type="h4" fontFamily="GTAM" fontSize="21px">
        {survey?.title}
      </Element>
      <Element type="p" fontFamily="GTAR" fontSize="14px">
        Please answer the below questions about your {lowerCase(survey?.title)}.
      </Element>
      <Element type="hr" backgroundColor={colors.wildSand} margin="16px 0" />
      <form style={{ width: survey?.width }} className={survey?.shortName}>
        {/* Rendering questions */}
        {renderSurvey()}

        <Flex align="center">
          <Button
            scheme="light"
            onClick={() => navigate('/landing/assessments')}
          >
            Cancel
          </Button>
          <Element
            type={Button}
            attrs={{ scheme: 'light', onClick: saveSurvey }}
            marginLeft="10%"
          >
            Save & Quit
          </Element>
          {currentStep + 1 !== 1 && (
            <Element
              type={Button}
              attrs={{
                scheme: 'dark',
                onClick: () => setCurrentStep(currentStep - 1)
              }}
              marginLeft="16px"
            >
              Previous
            </Element>
          )}
          {currentStep + 1 < steps?.length && (
            <Element
              type={Button}
              attrs={{
                scheme: 'dark',
                disabled: !surveyParser.isAllQuestionsAnswered(
                  steps[currentStep],
                  answers,
                  isOptionalAnswered
                ),
                onClick: gotoNextStep
              }}
              marginLeft="16px"
            >
              Next
            </Element>
          )}
          {currentStep + 1 === steps?.length && (
            <Element
              type={Button}
              attrs={{
                scheme: 'dark',
                disabled: !surveyParser.isAllQuestionsAnswered(
                  steps[currentStep],
                  answers,
                  isOptionalAnswered
                ),
                onClick: nextSurveyOrFinishSurvey
              }}
              marginLeft="16px"
              className={isFinishSurvey() ? '' : 'continue-survey'}
            >
              {isFinishSurvey() ? 'Finish' : 'Continue to Next Survey'}
            </Element>
          )}
        </Flex>
      </form>
    </>
  );
};

export default Survey;